import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanCatalog } from '@/presentation/hooks/usePlanCatalog';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { TogglePlanStatusAction } from '@/application/actions';
import { Button, ConfirmModal } from '@/presentation/components/common';
import { CreatePlanModal } from '@/presentation/components/plans/CreatePlanModal';
import { EditPlanModal } from '@/presentation/components/plans/EditPlanModal';
import { PlanFeaturesScroll } from '@/presentation/components/plans/PlanFeaturesScroll';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import type { PlanCatalogItem, ProductTier } from '@/domain/types/planCatalog';
import {
  FEATURE_GROUP_LABELS,
  PLAN_FEATURE_UI_GROUPS,
  TIER_LABELS,
  isRetiredIntermedioPlan,
} from '@/domain/types/planCatalog';

type ViewMode = 'cards' | 'matrix';

/** Respuesta de GET /plans/:id/deletion-impact */
type PlanDeletionImpact = {
  planId: number;
  nombre: string;
  clinicas: number;
  ejemplos: string[];
  periodosHistoricos: number;
};

const tierBadgeClass = (tier: ProductTier) => {
  if (tier === 'AVANZADO') return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200';
  return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200';
};

const IconEdit = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const IconPower = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
  </svg>
);

const IconTrash = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

type PlanAdminActionsProps = {
  plan: PlanCatalogItem;
  onEdit: () => void;
  onToggle: () => void;
  onDelete?: () => void;
  activateLabel: string;
  deactivateLabel: string;
  deleteLabel?: string;
  align?: 'start' | 'center' | 'end';
  isDeleting?: boolean;
};

const PlanAdminActions: React.FC<PlanAdminActionsProps> = ({
  plan,
  onEdit,
  onToggle,
  onDelete,
  activateLabel,
  deactivateLabel,
  deleteLabel = 'Eliminar',
  align = 'end',
  isDeleting = false,
}) => (
  <div
    className={`inline-flex items-center gap-0.5 ${
      align === 'center' ? 'justify-center' : align === 'start' ? 'justify-start' : 'justify-end'
    }`}
  >
    <button
      type="button"
      onClick={onEdit}
      title="Editar"
      disabled={isDeleting}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white transition-colors disabled:opacity-40"
    >
      <IconEdit />
    </button>
    <button
      type="button"
      onClick={onToggle}
      title={plan.planActivo ? deactivateLabel : activateLabel}
      disabled={isDeleting}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white transition-colors disabled:opacity-40"
    >
      <IconPower />
    </button>
    {onDelete && plan.id !== 1 && (
      <button
        type="button"
        onClick={onDelete}
        title={deleteLabel}
        disabled={isDeleting}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300 transition-colors disabled:opacity-40"
      >
        <IconTrash />
      </button>
    )}
  </div>
);

type TogglePlanStatusModalProps = {
  plan: PlanCatalogItem | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const TogglePlanStatusModal: React.FC<TogglePlanStatusModalProps> = ({
  plan,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  if (!plan) return null;

  const isDeactivating = plan.planActivo;
  const action = isDeactivating ? t('pages.plans.deactivate') : t('pages.plans.activate');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500/70"
          onClick={isLoading ? undefined : onClose}
        />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isDeactivating
                    ? t('pages.plans.toggleModalDeactivateTitle')
                    : t('pages.plans.toggleModalActivateTitle')}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('pages.plans.toggleModalQuestion', { action: action.toLowerCase(), name: plan.nombre })}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="text-gray-400 hover:text-gray-600 text-xl disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {plan.nombre}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                    plan.planActivo
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >
                  {plan.planActivo ? t('pages.plans.active') : t('pages.plans.inactive')}
                </span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              {isDeactivating
                ? t('pages.plans.toggleModalDeactivateHint')
                : t('pages.plans.toggleModalActivateHint')}
            </p>
          </div>

          <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant={isDeactivating ? 'danger' : 'primary'}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              {action}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Detalle de qué se lleva puesto el borrado de un plan. Se muestra dentro del
 * modal de confirmación, antes de una acción irreversible.
 */
const PlanDeletionImpactSummary: React.FC<{
  state: 'loading' | 'ready' | 'failed';
  impact: PlanDeletionImpact | null;
}> = ({ state, impact }) => {
  const { t } = useTranslation();

  if (state === 'loading') {
    return (
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('pages.plans.deleteImpactLoading')}
      </p>
    );
  }

  if (state === 'failed' || !impact) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
        {t('pages.plans.deleteImpactUnavailable')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {impact.clinicas > 0 ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
            {t('pages.plans.deleteImpactClinicsTitle', { total: impact.clinicas })}
          </p>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-200/90">
            {t('pages.plans.deleteImpactClinics')}
          </p>
          {impact.ejemplos.length > 0 ? (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300/80">
              {impact.ejemplos.slice(0, 3).join(' · ')}
              {impact.clinicas > impact.ejemplos.slice(0, 3).length ? ' …' : ''}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
          {t('pages.plans.deleteImpactNoClinics')}
        </div>
      )}

      {impact.periodosHistoricos > 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-slate-300">
          {t('pages.plans.deleteImpactHistory', { total: impact.periodosHistoricos })}
        </div>
      ) : null}
    </div>
  );
};

export const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { plans, isLoading, error, refetch, updateCapabilities } = usePlanCatalog();
  const { success, error: showError } = useToast();
  const { canAdminister } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanCatalogItem | null>(null);
  const [togglePlan, setTogglePlan] = useState<PlanCatalogItem | null>(null);
  const [isTogglingPlan, setIsTogglingPlan] = useState(false);
  const [deletingPlanId, setDeletingPlanId] = useState<number | null>(null);
  const [planToDelete, setPlanToDelete] = useState<PlanCatalogItem | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<PlanDeletionImpact | null>(null);
  const [impactState, setImpactState] = useState<'loading' | 'ready' | 'failed'>('loading');

  const sortedPlans = useMemo(
    () =>
      [...plans]
        .filter((plan) => !isRetiredIntermedioPlan(plan))
        .sort((a, b) => a.precio - b.precio || a.id - b.id),
    [plans],
  );

  const allFeatureKeys = useMemo(() => {
    const first = plans.find((p) => p.features?.length)?.features ?? [];
    return first.filter((feature) => PLAN_FEATURE_UI_GROUPS[feature.key] !== 'conexion');
  }, [plans]);

  const handleToggleStatus = async () => {
    if (!togglePlan) return;

    const plan = togglePlan;
    setIsTogglingPlan(true);
    try {
      const toggleAction = container.get<TogglePlanStatusAction>(TYPES.TogglePlanStatusAction);
      await toggleAction.execute(plan.id);
      success(plan.planActivo ? t('pages.plans.deactivatedSuccess') : t('pages.plans.activatedSuccess'));
      setTogglePlan(null);
      refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsTogglingPlan(false);
    }
  };

  /** Abre el modal y va a buscar el impacto: borrar es irreversible. */
  const handleDelete = async (plan: PlanCatalogItem) => {
    if (plan.id === 1) {
      showError(t('pages.plans.deleteBaseBlocked'));
      return;
    }
    setPlanToDelete(plan);
    setDeletionImpact(null);
    setImpactState('loading');
    try {
      const res = await httpClient.get<PlanDeletionImpact>(
        `/plans/${plan.id}/deletion-impact`,
      );
      setDeletionImpact(res.data ?? null);
      setImpactState(res.data ? 'ready' : 'failed');
    } catch {
      // Si el preview falla no bloqueamos la baja: se avisa y se decide igual.
      setImpactState('failed');
    }
  };

  const confirmDelete = async () => {
    const plan = planToDelete;
    if (!plan) return;
    setDeletingPlanId(plan.id);
    try {
      const deleteAction = container.get<{ execute: (id: number) => Promise<void> }>(TYPES.DeletePlanAction);
      await deleteAction.execute(plan.id);
      success(t('pages.plans.deletedSuccess', { name: plan.nombre }));
      setPlanToDelete(null);
      setDeletionImpact(null);
      refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setDeletingPlanId(null);
    }
  };

  if (isLoading && plans.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="panel-page panel-safe-bottom">
      <div className="panel-page-header">
        <h1 className="panel-page-title">
          {t('pages.plans.title')}
        </h1>
        <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
          <div className="flex h-8 overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              className={`h-full px-2.5 text-xs font-medium leading-none ${
                viewMode === 'cards'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode('matrix')}
              className={`h-full px-2.5 text-xs font-medium leading-none ${
                viewMode === 'matrix'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              Comparativa
            </button>
          </div>
          {canAdminister && (
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
              {t('pages.plans.create')}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      )}

      {sortedPlans.length === 0 ? (
        <div className="panel-card border-dashed px-4 py-10 text-center text-sm text-gray-500">
          {t('pages.plans.noResults')}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5 xl:gap-6 auto-rows-fr">
          {sortedPlans.map((plan) => {
            const comm = plan.communication;
            return (
              <article
                key={plan.id}
                className={`panel-card h-full flex flex-col overflow-hidden ${
                  plan.planActivo
                    ? ''
                    : 'border-dashed opacity-80'
                }`}
              >
                {/* Header fijo */}
                <div className="border-b border-gray-100 px-3.5 pb-2.5 pt-3 dark:border-gray-700 md:px-4 md:pb-2.5 md:pt-3 lg:px-5">
                  <div className="grid grid-cols-[1fr_auto] gap-2 md:gap-3 items-start">
                    <div className="min-w-0">
                      <div className="flex items-start gap-2">
                        <h2 className="text-base md:text-lg lg:text-xl font-bold text-slate-900 dark:text-white truncate leading-6 md:leading-7 min-h-6 md:min-h-7 flex-1">
                          {plan.nombre}
                        </h2>
                        {canAdminister && (
                          <PlanAdminActions
                            plan={plan}
                            onEdit={() => setEditingPlan(plan)}
                            onToggle={() => setTogglePlan(plan)}
                            onDelete={() => handleDelete(plan)}
                            activateLabel={t('pages.plans.activate')}
                            deactivateLabel={t('pages.plans.deactivate')}
                            deleteLabel={t('common.delete')}
                            isDeleting={deletingPlanId === plan.id}
                          />
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 h-6 overflow-hidden">
                        <span className={`panel-chip whitespace-nowrap ${tierBadgeClass(plan.productTier)}`}>
                          {TIER_LABELS[plan.productTier]}
                        </span>
                        <span
                          className={`panel-chip whitespace-nowrap ${
                            plan.planActivo
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {plan.planActivo ? t('pages.plans.active') : t('pages.plans.inactive')}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0 min-w-[5.5rem] md:min-w-[7rem] lg:min-w-[8rem]">
                      <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white tabular-nums leading-7 md:leading-8 lg:leading-9 h-7 md:h-8 lg:h-9 truncate">
                        ${plan.precio.toLocaleString('es-AR')}
                      </div>
                      <div className="text-[11px] md:text-xs text-gray-500 h-4 mt-0.5">/ mes</div>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-1.5 md:gap-2">
                    <div className="flex h-9 items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 md:px-3 dark:bg-gray-900/40">
                      <div className="min-w-0 truncate text-[10px] uppercase tracking-wide text-gray-500">
                        {t('pages.plans.consultorios')}
                      </div>
                      <div className="shrink-0 text-sm md:text-base font-semibold leading-none text-gray-900 tabular-nums dark:text-white">
                        {plan.consultorios}
                      </div>
                    </div>
                    <div className="flex h-9 items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 md:px-3 dark:bg-gray-900/40">
                      <div className="min-w-0 truncate text-[10px] uppercase tracking-wide text-gray-500">
                        {t('pages.plans.profesionales')}
                      </div>
                      <div className="shrink-0 text-sm md:text-base font-semibold leading-none text-gray-900 tabular-nums dark:text-white">
                        {plan.profesionales}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cuerpo */}
                <div className="px-3.5 py-2.5 md:px-4 md:py-2.5 lg:px-5 flex flex-col gap-2 flex-1">
                  <div>
                    <h3 className="text-[11px] md:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Canales
                    </h3>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {[
                        ['Email', comm?.channelEmail ?? false],
                      ].map(([label, on]) => (
                        <span
                          key={String(label)}
                          className={`text-[11px] md:text-xs px-2 py-1 rounded-md font-medium whitespace-nowrap ${
                            on
                              ? 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200'
                              : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 line-through'
                          }`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <PlanFeaturesScroll
                    title="Capacidades"
                    items={plan.features
                      .filter((feature) => PLAN_FEATURE_UI_GROUPS[feature.key] !== 'conexion')
                      .map((feature) => ({
                      key: feature.key,
                      label: feature.label,
                      enabled: feature.enabled,
                    }))}
                  />
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-auto max-h-[calc(100vh-11rem)]">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="sticky top-0 left-0 z-30 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[0_2px_0_0_rgba(15,23,42,0.12)] border-b border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:shadow-[0_2px_0_0_rgba(255,255,255,0.08)] min-w-[220px]">
                  Capacidad
                </th>
                {sortedPlans.map((plan) => (
                  <th
                    key={plan.id}
                    className="sticky top-0 z-20 bg-slate-50 px-4 py-3 text-center shadow-[0_2px_0_0_rgba(15,23,42,0.12)] border-b border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:shadow-[0_2px_0_0_rgba(255,255,255,0.08)] min-w-[150px]"
                  >
                    <div className="font-semibold text-gray-900 dark:text-white">{plan.nombre}</div>
                    <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${tierBadgeClass(plan.productTier)}`}>
                      {TIER_LABELS[plan.productTier]}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                      ${plan.precio.toLocaleString('es-AR')}
                    </div>
                    {canAdminister && (
                      <div className="mt-2 flex justify-center">
                        <PlanAdminActions
                          plan={plan}
                          align="center"
                          onEdit={() => setEditingPlan(plan)}
                          onToggle={() => setTogglePlan(plan)}
                          onDelete={() => handleDelete(plan)}
                          activateLabel={t('pages.plans.activate')}
                          deactivateLabel={t('pages.plans.deactivate')}
                          deleteLabel={t('common.delete')}
                          isDeleting={deletingPlanId === plan.id}
                        />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50/80 dark:bg-gray-900/20">
                <td className="sticky left-0 z-10 bg-slate-50 dark:bg-gray-800 px-4 py-2 font-medium text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700">
                  Cupos
                </td>
                {sortedPlans.map((plan) => (
                  <td key={plan.id} className="px-4 py-2 text-center text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
                    {plan.consultorios} cons. · {plan.profesionales} prof.
                  </td>
                ))}
              </tr>
              {allFeatureKeys.map((feature) => (
                <tr key={feature.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 group">
                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/30 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="font-medium text-gray-900 dark:text-white">{feature.label}</div>
                    <div className="text-[11px] text-gray-500">
                      {FEATURE_GROUP_LABELS[feature.group] ?? feature.group} · {TIER_LABELS[feature.minTier]}+
                    </div>
                  </td>
                  {sortedPlans.map((plan) => {
                    const enabled = plan.features.find((f) => f.key === feature.key)?.enabled ?? false;
                    return (
                      <td key={plan.id} className="px-4 py-2 text-center border-b border-gray-100 dark:border-gray-700">
                        {enabled ? (
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 font-bold">
                            ✓
                          </span>
                        ) : (
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 text-xs">
                            *
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreatePlanModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {editingPlan && (
        <EditPlanModal
          key={editingPlan.id}
          isOpen={!!editingPlan}
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onUpdateCapabilities={updateCapabilities}
          onSuccess={() => {
            setEditingPlan(null);
            refetch();
          }}
        />
      )}

      <TogglePlanStatusModal
        plan={togglePlan}
        isLoading={isTogglingPlan}
        onClose={() => {
          if (!isTogglingPlan) setTogglePlan(null);
        }}
        onConfirm={handleToggleStatus}
      />

      <ConfirmModal
        isOpen={planToDelete != null}
        onClose={() => {
          if (deletingPlanId != null) return;
          setPlanToDelete(null);
          setDeletionImpact(null);
        }}
        onConfirm={confirmDelete}
        title={t('pages.plans.deleteTitle', { name: planToDelete?.nombre ?? '' })}
        message={t('pages.plans.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={deletingPlanId != null}
      >
        <PlanDeletionImpactSummary state={impactState} impact={deletionImpact} />
      </ConfirmModal>
    </div>
  );
};
